/**
 * Date & Time defaults section.
 *
 * Extracted from AdminSettingsPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import type { OrgCaseDefaults, UpdateOrgCaseDefaultsRequest } from '../../types/case-container'
import { DefaultsField } from './DefaultsField'

const DECIMAL_BASE = 10

interface DateTimeDefaultsSectionProps {
  orgDefaults: OrgCaseDefaults | null
  formData: UpdateOrgCaseDefaultsRequest
  setFormData: (data: UpdateOrgCaseDefaultsRequest) => void
  editMode: boolean
}

const SELECT_CLASS =
  'w-full px-3 py-2 border border-[var(--border)] rounded-lg bg-[var(--bg-card)] text-[var(--text)] text-sm'

function DateFormatField({
  orgDefaults,
  formData,
  setFormData,
  editMode,
}: DateTimeDefaultsSectionProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-[var(--text-dim)]">
        {t('adminSettings.dateFormat')}
      </label>
      {editMode ? (
        <select
          value={formData.dateFormat ?? ''}
          onChange={(e) => {
            setFormData({ ...formData, dateFormat: e.target.value || null })
          }}
          className={SELECT_CLASS}
        >
          <option value="">{t('adminSettings.selectFormat')}</option>
          <option value="YYYY-MM-DD">ISO (YYYY-MM-DD)</option>
          <option value="MM/DD/YYYY">US (MM/DD/YYYY)</option>
          <option value="DD/MM/YYYY">EU (DD/MM/YYYY)</option>
          <option value="DD-MM-YYYY">AU (DD-MM-YYYY)</option>
        </select>
      ) : (
        <p className="text-sm text-[var(--text)]">
          {orgDefaults?.dateFormat ?? t('adminSettings.notSet')}
        </p>
      )}
    </div>
  )
}

function TimeFormatField({
  orgDefaults,
  formData,
  setFormData,
  editMode,
}: DateTimeDefaultsSectionProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-[var(--text-dim)]">
        {t('adminSettings.timeFormat')}
      </label>
      {editMode ? (
        <select
          value={formData.timeFormat ?? ''}
          onChange={(e) => {
            setFormData({ ...formData, timeFormat: e.target.value || null })
          }}
          className={SELECT_CLASS}
        >
          <option value="">{t('adminSettings.selectFormat')}</option>
          <option value="24h">{t('adminSettings.twentyFourHour')}</option>
          <option value="12h">{t('adminSettings.twelveHour')}</option>
        </select>
      ) : (
        <p className="text-sm text-[var(--text)]">
          {orgDefaults?.timeFormat ?? t('adminSettings.notSet')}
        </p>
      )}
    </div>
  )
}

function RetentionField({
  orgDefaults,
  formData,
  setFormData,
  editMode,
}: DateTimeDefaultsSectionProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-[var(--text-dim)]">
        {t('adminSettings.retentionPeriod')}
      </label>
      {editMode ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={formData.retentionPeriodDays ?? ''}
            onChange={(e) => {
              setFormData({
                ...formData,
                retentionPeriodDays: e.target.value ? parseInt(e.target.value, DECIMAL_BASE) : null,
              })
            }}
            className="w-24 rounded-lg border border-[var(--border)] bg-[var(--bg-card)] px-3 py-2 text-sm text-[var(--text)]"
          />
          <span className="text-sm text-[var(--text-muted)]">{t('adminSettings.days')}</span>
        </div>
      ) : (
        <p className="text-sm text-[var(--text)]">
          {orgDefaults?.retentionPeriodDays
            ? `${orgDefaults.retentionPeriodDays.toString()} ${t('adminSettings.days')}`
            : t('adminSettings.notSet')}
        </p>
      )}
    </div>
  )
}

export function DateTimeDefaultsSection(props: DateTimeDefaultsSectionProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-3 border-t border-[var(--border)] pt-4">
      <h4 className="text-sm font-medium text-[var(--text-dim)]">
        {t('adminSettings.dateTimeDefaults')}
      </h4>
      <div className="grid gap-4 sm:grid-cols-2">
        <DefaultsField
          label={t('adminSettings.defaultTimezone')}
          value={props.orgDefaults?.timezone}
          editValue={props.formData.timezone ?? ''}
          editMode={props.editMode}
          onChange={(v) => {
            props.setFormData({ ...props.formData, timezone: v || null })
          }}
        />
        <DateFormatField {...props} />
        <TimeFormatField {...props} />
        <RetentionField {...props} />
      </div>
    </div>
  )
}
